import { useEffect, useRef, useState } from "react";
import { useEditor, EditorContent, type JSONContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Table from "@tiptap/extension-table";
import TableRow from "@tiptap/extension-table-row";
import TableCell from "@tiptap/extension-table-cell";
import TableHeader from "@tiptap/extension-table-header";
import UnderlineExt from "@tiptap/extension-underline";
import TextAlign from "@tiptap/extension-text-align";
import Highlight from "@tiptap/extension-highlight";
import Placeholder from "@tiptap/extension-placeholder";
import { ProtocolEditorToolbar } from "./ProtocolEditorToolbar";
import { CommentMark } from "./CommentMark";
import { CommentPopover } from "./CommentPopover";
import { MarginCommentPanel } from "./MarginCommentPanel";
import type { ReviewComment } from "@/api/protocol-types";

interface MarginComment extends ReviewComment {
  textRange?: { from: number; to: number; text: string };
}

interface ProtocolTipTapEditorProps {
  initialContent: JSONContent;
  onUpdate?: (content: JSONContent) => void;
  comments?: MarginComment[];
  onAddComment?: (params: {
    content: string;
    mentionedUids: string[];
    textRange: { from: number; to: number; text: string };
  }) => void;
  onResolveComment?: (commentId: string) => void;
  onReplyComment?: (
    commentId: string,
    content: string,
    mentionedUids: string[],
  ) => void;
}

export function ProtocolTipTapEditor({
  initialContent,
  onUpdate,
  comments = [],
  onAddComment,
  onResolveComment,
  onReplyComment,
}: ProtocolTipTapEditorProps) {
  const debounceRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  const onUpdateRef = useRef(onUpdate);
  onUpdateRef.current = onUpdate;

  const [activeCommentId, setActiveCommentId] = useState<string | null>(null);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [1, 2, 3, 4] },
      }),
      Table.configure({ resizable: true }),
      TableRow,
      TableCell,
      TableHeader,
      UnderlineExt,
      TextAlign.configure({
        types: ["heading", "paragraph"],
      }),
      Highlight,
      Placeholder.configure({
        placeholder: "Start writing your protocol...",
      }),
      CommentMark,
    ],
    content: initialContent,
    onUpdate: ({ editor: e }) => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        onUpdateRef.current?.(e.getJSON());
      }, 500);
    },
    editorProps: {
      attributes: {
        class:
          "prose prose-sm dark:prose-invert max-w-none px-12 py-8 min-h-[600px] focus:outline-none",
      },
    },
  });

  // Sync content when initialContent changes
  const isFirstRender = useRef(true);
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    if (editor && initialContent) {
      editor.commands.setContent(initialContent);
    }
  }, [initialContent, editor]);

  // Cleanup
  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  const handleAddComment = (params: {
    content: string;
    mentionedUids: string[];
    textRange: { from: number; to: number; text: string };
  }) => {
    // Apply comment mark to selected text
    if (editor) {
      const tempId = `temp-${Date.now()}`;
      editor
        .chain()
        .focus()
        .setComment(tempId)
        .run();
    }
    onAddComment?.(params);
  };

  const handleClickComment = (commentId: string) => {
    setActiveCommentId(commentId);
    // Scroll to highlighted text in editor
    if (editor) {
      const { doc } = editor.state;
      let found = false;
      doc.descendants((node, pos) => {
        if (found) return false;
        node.marks.forEach((mark) => {
          if (
            mark.type.name === "commentMark" &&
            mark.attrs.commentId === commentId
          ) {
            editor.commands.setTextSelection(pos);
            editor.commands.scrollIntoView();
            found = true;
          }
        });
        return !found;
      });
    }
  };

  return (
    <div className="flex flex-col">
      {/* Toolbar + comment button */}
      <div className="flex items-center">
        <div className="flex-1">
          <ProtocolEditorToolbar editor={editor} />
        </div>
        {editor && onAddComment && (
          <div className="border-b border-border bg-muted/50 px-2 py-1.5">
            <CommentPopover
              editor={editor}
              onAddComment={handleAddComment}
            />
          </div>
        )}
      </div>

      {/* Editor + margin layout */}
      <div className="flex flex-1 overflow-hidden">
        {/* Document area */}
        <div className="flex-1 overflow-y-auto bg-gray-100 dark:bg-zinc-900/50">
          <div className="mx-auto my-8 max-w-[816px] rounded-sm border border-border bg-white shadow-lg dark:bg-card">
            {/* Running header */}
            <div className="border-b border-gray-200 px-12 py-2 text-[9px] text-gray-400 dark:border-border dark:text-muted-foreground">
              CONFIDENTIAL — DRAFT
            </div>

            {/* Editor content */}
            <EditorContent editor={editor} />

            {/* Running footer */}
            <div className="border-t border-gray-200 px-12 py-2 text-[9px] text-gray-400 dark:border-border dark:text-muted-foreground">
              Protocol Document
            </div>
          </div>
        </div>

        {/* Right margin comments panel */}
        <div className="w-72 shrink-0 overflow-y-auto border-l border-border bg-card/30">
          <div className="border-b border-border px-3 py-2">
            <span className="text-[11px] font-semibold text-foreground">
              Comments
            </span>
            {comments.length > 0 && (
              <span className="ml-1.5 text-[10px] text-muted-foreground">
                ({comments.length})
              </span>
            )}
          </div>
          <MarginCommentPanel
            comments={comments}
            activeCommentId={activeCommentId}
            onClickComment={handleClickComment}
            onResolve={(id) => onResolveComment?.(id)}
            onReply={(id, content, uids) =>
              onReplyComment?.(id, content, uids)
            }
          />
        </div>
      </div>
    </div>
  );
}
